/**
 * What the recipe does when a required figure cannot be found (DOP-456).
 *
 * Lifted out of `MetricsView` in DOP-480: it is a setting, not a metric, so it
 * belongs on the dashboard's Settings tab beside the cadence — and a control
 * that lives in one file cannot end up meaning two different things on two
 * tabs.
 *
 * Saved on the click with no Save button: it is one setting of two, and the
 * radio's own state is the confirmation. The selection is read from the
 * `policy` prop on every render and never copied into local state — a local
 * copy would go on showing the click after a save failed, which is the one
 * moment the setting has to be believable.
 */

import { Lock } from "lucide-react";
import React from "react";
import type { MonitoringMissingDataPolicy } from "../../types/monitoring";
import MonitoringSettingsCard from "./MonitoringSettingsCard";

interface PolicyRowProps {
	policy: MonitoringMissingDataPolicy;
	label: string;
	description: string;
	selected: boolean;
	disabled: boolean;
	onSelect: (policy: MonitoringMissingDataPolicy) => void;
}

const PolicyRow: React.FC<PolicyRowProps> = ({
	policy,
	label,
	description,
	selected,
	disabled,
	onSelect,
}) => (
	<label className="flex items-start gap-2 py-1">
		<input
			type="radio"
			// One `name` for both rows, so they are one group to the browser and
			// to a keyboard: arrow keys move between the two policies.
			name="monitoring-missing-data-policy"
			value={policy}
			className="mt-1 border-gray-300 text-[#008782] focus:ring-[#008782] disabled:cursor-not-allowed disabled:opacity-60"
			checked={selected}
			disabled={disabled}
			onChange={() => onSelect(policy)}
		/>
		<span className="min-w-0">
			<span className="block text-base text-text-base">{label}</span>
			<span className="block text-sm text-text-muted">{description}</span>
		</span>
	</label>
);

export interface MissingDataPolicyCardProps {
	policy: MonitoringMissingDataPolicy;
	/** Called with the policy the user picked. The page saves it (DOP-456). */
	onChange: (policy: MonitoringMissingDataPolicy) => void;
	/** A save is in flight: the radios are frozen until it lands. */
	saving?: boolean;
}

const MissingDataPolicyCard: React.FC<MissingDataPolicyCardProps> = ({
	policy,
	onChange,
	saving = false,
}) => (
	<MonitoringSettingsCard title="When a figure is missing">
		<PolicyRow
			policy="run_and_flag"
			label="Run and flag"
			description="Produce the workbook with the gap marked, and list it as work to do."
			selected={policy === "run_and_flag"}
			disabled={saving}
			onSelect={onChange}
		/>
		<PolicyRow
			policy="refuse"
			label="Refuse to run"
			description="Generate as usual, but refuse to export a workbook until every required figure is in."
			selected={policy === "refuse"}
			disabled={saving}
			onSelect={onChange}
		/>
		<p className="mt-3 flex items-start gap-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-text-muted">
			<Lock className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
			<span>A period cannot be locked while a gap is open.</span>
		</p>
	</MonitoringSettingsCard>
);

export default MissingDataPolicyCard;
